import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X, User, Phone, Mail } from "lucide-react";

/**
 * Selector de cliente con búsqueda por nombre, teléfono o email.
 * Diseño mobile-first con dropdown personalizado (no usa <select> nativo).
 */
export default function CustomerSelect({
  customers = [],
  value = "",
  onChange,
  placeholder = "Buscar cliente por nombre, teléfono o email…",
  disabled = false,
  testid = "customer-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => customers.find((c) => c.id === value) || null,
    [customers, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => {
      const hay = `${c.name || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [customers, query]);

  useEffect(() => {
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("touchstart", onDoc);
    }
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  function pick(id) {
    onChange && onChange(id);
    setOpen(false);
    setQuery("");
  }

  function clearSelection(e) {
    e.stopPropagation();
    onChange && onChange("");
    setQuery("");
  }

  return (
    <div className="relative" ref={wrapperRef} data-testid={testid}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-trigger`}
        className={`input-base flex items-center gap-2 w-full text-left ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <User size={16} className="text-slate-400 shrink-0" />
        <div className="flex-1 min-w-0">
          {selected ? (
            <div className="truncate">
              <span className="font-semibold text-slate-800">{selected.name}</span>
              {selected.phone && <span className="text-slate-500 text-xs ml-2">· {selected.phone}</span>}
            </div>
          ) : (
            <span className="text-slate-400 text-sm">{placeholder}</span>
          )}
        </div>
        {selected && !disabled && (
          <span
            onClick={clearSelection}
            className="p-1 hover:bg-slate-200 rounded-full transition-colors"
            data-testid={`${testid}-clear`}
          >
            <X size={14} className="text-slate-500" />
          </span>
        )}
        <ChevronDown size={16} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
          data-testid={`${testid}-dropdown`}
        >
          {/* Search input */}
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nombre, teléfono, email…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-blue-400"
                data-testid={`${testid}-search-input`}
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1 px-1">
              {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
              {customers.length > filtered.length && ` de ${customers.length}`}
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-72 overflow-y-auto" role="listbox">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-500 text-center" data-testid={`${testid}-empty`}>
                Sin resultados para "<b>{query}</b>"
              </div>
            ) : (
              filtered.map((c) => {
                const isSelected = c.id === value;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pick(c.id)}
                    data-testid={`${testid}-option-${c.id}`}
                    className={`w-full text-left px-3 py-2.5 hover:bg-blue-50 transition-colors border-b border-slate-50 last:border-b-0 flex items-start gap-2 ${
                      isSelected ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${isSelected ? "font-bold text-blue-700" : "font-semibold text-slate-800"}`}>
                        {c.name}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-slate-500">
                        {c.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={11} /> {c.phone}
                          </span>
                        )}
                        {c.email && (
                          <span className="flex items-center gap-1 truncate max-w-[180px]">
                            <Mail size={11} /> {c.email}
                          </span>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <div className="text-blue-600 text-xs font-mono uppercase shrink-0">✓</div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
